import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { auth, googleProvider, db } from "../index";
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserSessionPersistence
} from "firebase/auth";
import { collection, doc, setDoc, getDoc, serverTimestamp, updateDoc, onSnapshot } from "firebase/firestore";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [is2FAVerified, setIs2FAVerified] = useState(false);

  // [NEW] Admin data including role and institute
  const [adminData, setAdminData] = useState(null);
  const [originalAdminData, setOriginalAdminData] = useState(null); // [NEW] Impersonation tracking

  // [SECURITY] Banking Grade: Session Monitor
  const sessionMonitorUnsubscribe = useRef(null);

  const set2FAStatus = (status) => {
    setIs2FAVerified(status);
    if (status) {
      sessionStorage.setItem('is2FAVerified', 'true');
    } else {
      sessionStorage.removeItem('is2FAVerified');
    }
  };

  // [NEW] Determine query scope based on admin role
  const queryScope = useMemo(() => {
    if (!adminData) return null;

    if (adminData.role === 'SUPER_ADMIN') {
      // Super Admin sees all data from all institutes
      return { type: 'all' };
    } else if (adminData.role === 'INSTITUTE_ADMIN') {
      // Institute Admin sees only their institute's data
      return {
        type: 'institute',
        instituteId: adminData.instituteId,
        instituteName: adminData.instituteName || adminData.instituteId
      };
    } else if (adminData.role === 'HOSTEL_ADMIN') {
      // Hostel Admin sees only specific hostels within their institute
      return {
        type: 'hostel',
        instituteId: adminData.instituteId,
        instituteName: adminData.instituteName || adminData.instituteId,
        hostels: adminData.allowedHostels || []
      };
    }

    // Default fallback - deny all
    return null;
  }, [adminData]);

  // [NEW] Check if current admin is Super Admin
  const isSuperAdmin = useMemo(() => {
    return adminData?.role === 'SUPER_ADMIN';
  }, [adminData]);

  // [NEW] Fetch admin data from Firestore
  const fetchAdminData = useCallback(async (userId) => {
    try {
      const adminRef = doc(db, "admins", userId);
      const adminSnap = await getDoc(adminRef);

      if (adminSnap.exists()) {
        const data = adminSnap.data();
        setAdminData({
          uid: userId,
          email: data.email,
          name: data.name,
          role: data.role || 'INSTITUTE_ADMIN',
          instituteId: data.instituteId || null,
          instituteName: data.instituteName || null,
          // [FIX] Normalize allowedHostels from whichever field the document uses:
          //   1. data.hostels (array)  ← current format used by Admin Management UI
          //   2. data.allowedHostels (array) ← alternate naming fallback
          //   3. data.hostel (string) ← legacy singular-field fallback
          allowedHostels: (
            (Array.isArray(data.hostels) && data.hostels.length > 0)
              ? data.hostels
              : (Array.isArray(data.allowedHostels) && data.allowedHostels.length > 0)
                ? data.allowedHostels
                : (typeof data.hostel === 'string' && data.hostel.trim()
                  ? [data.hostel.trim()]
                  : null)
          ),
          lastLogin: data.lastLoginTime
        });
        // Warn developers when a HOSTEL_ADMIN has no hostel assignments configured
        if (data.role === 'HOSTEL_ADMIN' && !data.hostels?.length && !data.allowedHostels?.length && !data.hostel) {
          console.warn(
            `[AuthContext] HOSTEL_ADMIN uid=${userId} has no hostels array in their admin document. ` +
            `Add  hostels: ["<HostelName>"]  to /admins/${userId} in the Firebase console.`
          );
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching admin data:", error);
      return false;
    }
  }, []);

  // [NEW] Impersonation Functions
  const impersonate = useCallback((targetInstituteId, targetName = 'Impersonated User') => {
    if (!adminData || adminData.role !== 'SUPER_ADMIN') {
      console.warn("Only Super Admins can impersonate.");
      return;
    }
    setOriginalAdminData(adminData);
    setAdminData({
      uid: adminData.uid,
      email: adminData.email,
      name: `(Impersonating) ${targetName}`,
      role: 'INSTITUTE_ADMIN',
      instituteId: targetInstituteId,
      instituteName: `Impersonated Institute - ${targetInstituteId}`,
      allowedHostels: null,
      isImpersonating: true,
      impersonatedRole: 'INSTITUTE_ADMIN'
    });
  }, [adminData]);

  // [NEW] Hostel-Admin Impersonation
  const impersonateHostelAdmin = useCallback((targetInstituteId, targetHostels, targetName = 'Impersonated Hostel Admin') => {
    if (!adminData || adminData.role !== 'SUPER_ADMIN') {
      console.warn("Only Super Admins can impersonate.");
      return;
    }
    setOriginalAdminData(adminData);
    setAdminData({
      uid: adminData.uid,
      email: adminData.email,
      name: `(Impersonating) ${targetName}`,
      role: 'HOSTEL_ADMIN',
      instituteId: targetInstituteId,
      instituteName: `Impersonated Institute - ${targetInstituteId}`,
      allowedHostels: Array.isArray(targetHostels) ? targetHostels : [targetHostels],
      isImpersonating: true,
      impersonatedRole: 'HOSTEL_ADMIN'
    });
  }, [adminData]);

  const revertImpersonation = useCallback(() => {
    if (originalAdminData) {
      setAdminData(originalAdminData);
      setOriginalAdminData(null);
    }
  }, [originalAdminData]);

  async function login() {
    try {
      await setPersistence(auth, browserSessionPersistence);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const adminRef = doc(db, "admins", user.uid);
      const adminSnap = await getDoc(adminRef);

      if (adminSnap.exists()) {
        // [NEW] Fetch and store admin data
        await fetchAdminData(user.uid);

        // [SECURITY] Single Session Enforcement
        // 1. Generate unique ID for THIS session
        const currentSessionId = crypto.randomUUID();
        sessionStorage.setItem('sessionId', currentSessionId);

        // 2. Write this ID to the Admin Record (Overwriting any previous session)
        await updateDoc(adminRef, {
          currentSessionId: currentSessionId,
          lastLoginIP: "Captured via Cloud Function", // Placeholder
          lastLoginTime: serverTimestamp()
        });

        // 3. Log the history (Audit Trail)
        const sessionRef = doc(collection(db, "userSessions", user.uid, "sessions"));
        await setDoc(sessionRef, {
          loginTime: serverTimestamp(),
          deviceInfo: navigator.userAgent,
          sessionId: currentSessionId,
          ip: "Captured via Rules/Cloud Function"
        });
        return user;
      } else {
        await signOut(auth);
        throw new Error("Access Denied: You are not an administrator.");
      }
    } catch (error) {
      console.error("Login failed", error);
      throw error;
    }
  }

  function logout() {
    set2FAStatus(false);
    sessionStorage.removeItem('sessionId');
    setAdminData(null); // [NEW] Clear admin data on logout
    setOriginalAdminData(null);

    // Detach listener
    if (sessionMonitorUnsubscribe.current) {
      sessionMonitorUnsubscribe.current();
      sessionMonitorUnsubscribe.current = null;
    }

    return signOut(auth);
  }

  // [SECURITY] Banking Grade: Auto-Logout on Inactivity
  useEffect(() => {
    if (!currentUser) return;

    // 10 Minutes Inactivity Timer (Reduced for Banking Grade)
    const TIMEOUT_MS = 60 * 60 * 1000;
    let timeoutId;

    const handleActivity = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Only warn/logout if we are actually authenticated
        if (auth.currentUser) {
          console.warn("Session expired due to inactivity.");
          logout()
            .then(() => {
              alert("Session Expired: For your security, you have been logged out due to 10 minutes of inactivity.");
              window.location.href = '/login';
            })
            .catch(err => console.error("Logout error", err));
        }
      }, TIMEOUT_MS);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, handleActivity));
    handleActivity();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach(event => window.removeEventListener(event, handleActivity));
    };
  }, [currentUser]);

  // [SECURITY] Banking Grade: Concurrent Session Listener
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // [NEW] Fetch admin data on auth state change
        const isAdmin = await fetchAdminData(user.uid);

        // Non-admin users are simply denied access (no temp admin fallback)

        // Restore 2FA state
        const sessionVerified = sessionStorage.getItem('is2FAVerified') === 'true';
        setIs2FAVerified(sessionVerified);

        // Start Listening to the Admin Document for Session ID changes
        // Skip for temp admins — they don't have an admin document
        if (isAdmin) {
          const adminRef = doc(db, "admins", user.uid);

          // If a listener exists, kill it first
          if (sessionMonitorUnsubscribe.current) sessionMonitorUnsubscribe.current();

          sessionMonitorUnsubscribe.current = onSnapshot(adminRef, (docSnap) => {
            if (docSnap.exists()) {
              const dbSessionId = docSnap.data().currentSessionId;
              const localSessionId = sessionStorage.getItem('sessionId');

              // If DB has a session ID, and it differs from ours, we have been kicked.
              if (dbSessionId && localSessionId && dbSessionId !== localSessionId) {
                console.warn("Security Alert: Concurrent session detected.");
                logout().then(() => {
                  alert("Security Alert: You have been logged out because a new session was started on another device/browser.");
                });
              }
            }
          });
        }

      } else {
        set2FAStatus(false);
        setAdminData(null); // [NEW] Clear admin data when user logs out
        if (sessionMonitorUnsubscribe.current) {
          sessionMonitorUnsubscribe.current();
        }
      }

      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (sessionMonitorUnsubscribe.current) sessionMonitorUnsubscribe.current();
    };
  }, [fetchAdminData]);

  const value = {
    currentUser,
    login,
    logout,
    is2FAVerified,
    set2FAVerified: set2FAStatus,
    // [NEW] Multi-tenant properties
    adminData,
    queryScope,
    isSuperAdmin,
    impersonate,
    impersonateHostelAdmin, // [NEW] Hostel-admin impersonation
    revertImpersonation,
    isImpersonating: !!adminData?.isImpersonating,
    impersonatedRole: adminData?.impersonatedRole || null // [NEW] Role being impersonated
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
